import { useEffect } from 'react';

import { isApiEnabled } from './config';
import { resolveDeviceId } from './device-id';
import {
  markDeviceSessionFailed,
  markDeviceSessionReady,
} from './device-session';

import type { DeviceIdentityOutcome, UseDeviceIdentityDeps } from './types';

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 300_000;
/** `online` / foreground may pull an attempt forward at most this often (dou: 60 s). */
const EVENT_RETRY_THROTTLE_MS = 60_000;

/**
 * One attempt shared by every mounted hook. React StrictMode runs the effect
 * twice in dev; both effects await the same promise instead of registering
 * the device twice.
 */
let sharedAttempt: Promise<DeviceIdentityOutcome> | null = null;
const attemptOnce = (deps: UseDeviceIdentityDeps) =>
  (sharedAttempt ??= (async () => {
    try {
      return await deps.ensure(await deps.resolveDeviceId());
    } finally {
      sharedAttempt = null;
    }
  })());

const defaultDeps: UseDeviceIdentityDeps = {
  enabled: isApiEnabled,
  resolveDeviceId,
  // Lazy: `device-identity` → `transport` → lemon-web-core (axios, crypto-js,
  // jwt-decode). Loaded only when enabled, so a prod build with no backend
  // never ships it in the boot chunk.
  ensure: async (deviceId) =>
    (await import('./device-identity')).ensureDeviceIdentity(deviceId),
};

/**
 * Boot-time Device Identity: restore or register once, silently.
 *
 * - No-op when no backend is configured (`API_ENDPOINT` empty).
 * - Never blocks render and never throws — the diagnosis runs to a result without the server.
 *   (It is not local-*only* any more: submits and take uploads ride on this identity. They
 *   just fail quietly when it is missing.)
 * - On failure, retries with doubling backoff (1 s → 5 min cap). Coming back
 *   online or to the foreground pulls the next attempt forward, throttled to
 *   once per minute so a dead endpoint is not hammered. Single-flight.
 *
 * `deps` is an injection point for tests; production callers pass nothing.
 */
export const useDeviceIdentity = (
  deps: UseDeviceIdentityDeps = defaultDeps,
): void => {
  useEffect(() => {
    if (!deps.enabled()) return;

    let cancelled = false;
    let established = false;
    let inFlight = false;
    let attempt = 0;
    let lastAttemptAt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const scheduleRetry = () => {
      clearTimer();
      const delay = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS);
      attempt += 1;
      timer = setTimeout(() => void run(), delay);
    };

    const run = async () => {
      if (cancelled || established || inFlight) return;
      inFlight = true;
      lastAttemptAt = Date.now();
      try {
        const outcome = await attemptOnce(deps);
        if (cancelled) return;
        established = true;
        // 서명된 요청(프로필 조회·저장)이 이 신호를 기다린다.
        markDeviceSessionReady();
        console.info(
          `[device-identity] ${outcome.status}${
            outcome.status === 'registered' && outcome.uid
              ? ` ${outcome.uid}`
              : ''
          }`,
        );
      } catch (err) {
        console.warn('[device-identity] attempt failed, will retry', err);
        // 세션을 기다리던 요청(부팅 프로필 복원)이 상한을 채우지 않고 폴백으로 가게 한다.
        markDeviceSessionFailed();
        if (!cancelled) scheduleRetry();
      } finally {
        inFlight = false;
      }
    };

    const retryNow = () => {
      if (established || inFlight) return;
      if (Date.now() - lastAttemptAt < EVENT_RETRY_THROTTLE_MS) return;
      clearTimer();
      void run();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') retryNow();
    };

    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void run();

    return () => {
      cancelled = true;
      clearTimer();
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [deps]);
};
